import { useMemo } from 'react';

import CheckboxCell from '@/components/ui-v2/checkbox-cell';
import {
  areAllTableRowCheckboxesDisabled,
  computeLeafCheckboxSelectionSetsFromFlatRows,
} from '@/components/ui-v2/table-checkbox-utils';
import { Checkbox } from '@/components/ui/checkbox';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import { cn } from '@/lib/utils';
import { lastClickedRowState } from '@/states/last-clicked-row.state';
import { productsMapState } from '@/states/products.state';
import {
  clearSelection,
  isRowSelected,
  rowSelectionModelState,
} from '@/states/row-selection.state';
import { Table } from '@tanstack/react-table';
import { useAtomValue, useSetAtom, useStore } from 'jotai';

import { ExtendedColumnDef } from '../table-products';

type SelectAllCheckboxHeaderProps = {
  table: Table<DealItemWithVersions>;
  store: ReturnType<typeof useStore>;
  isOfferLocked: boolean;
};

/** Recalcul des ids sélectionnables seulement si filtres / map / lock changent, pas à chaque clic de sélection. */
function SelectAllCheckboxHeader({
  table,
  store,
  isOfferLocked,
}: SelectAllCheckboxHeaderProps) {
  const productsMap = useAtomValue(productsMapState);
  const model = useAtomValue(rowSelectionModelState);
  const setModel = useSetAtom(rowSelectionModelState);
  const filteredModel = table.getFilteredRowModel();

  const { selectableIds, excludedWhenSelectAll } = useMemo(
    () =>
      computeLeafCheckboxSelectionSetsFromFlatRows(
        filteredModel.flatRows,
        productsMap,
        isOfferLocked
      ),
    [filteredModel, isOfferLocked, productsMap]
  );

  const rows = table.getRowModel().rows;
  const headerCheckboxDisabled = useMemo(
    () =>
      isOfferLocked ||
      areAllTableRowCheckboxesDisabled(rows, store, isOfferLocked, productsMap),
    [isOfferLocked, rows, store, productsMap]
  );

  const allSelectableSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => isRowSelected(model, id));
  const someSelectableSelected =
    selectableIds.some((id) => isRowSelected(model, id)) &&
    !allSelectableSelected;

  return (
    <div
      className={cn(
        'flex w-full items-center justify-center p-2',
        (isOfferLocked || headerCheckboxDisabled) &&
          'cursor-not-allowed opacity-50'
      )}
      data-user-guide="Select All"
    >
      <Checkbox
        data-testid="checkbox-cell"
        checked={
          allSelectableSelected
            ? true
            : someSelectableSelected
              ? 'indeterminate'
              : false
        }
        onCheckedChange={(value) => {
          if (isOfferLocked || headerCheckboxDisabled) return;
          if (value === false) {
            setModel(clearSelection());
          } else {
            setModel({
              mode: 'all',
              excluded: excludedWhenSelectAll,
            });
          }
        }}
        id="select-all"
        aria-label="Select all products"
        disabled={isOfferLocked || headerCheckboxDisabled}
      />
    </div>
  );
}

const useColumnDefCheckbox = (): ExtendedColumnDef<DealItemWithVersions> => {
  const accessorKey = '_checkbox';
  const isOfferLocked = useIsOfferLocked();
  const setLastClickedRow = useSetAtom(lastClickedRowState);
  const store = useStore();

  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'Select',
      header: ({ table }: { table: Table<DealItemWithVersions> }) => (
        <SelectAllCheckboxHeader
          table={table}
          store={store}
          isOfferLocked={isOfferLocked}
        />
      ),
      cell: ({ row, table }) => (
        <CheckboxCell
          row={row}
          table={table}
          isOfferLocked={isOfferLocked}
          setLastClickedRow={setLastClickedRow}
          store={store}
        />
      ),
      size: 48,
    }),
    [isOfferLocked, setLastClickedRow, store]
  );
};

export default useColumnDefCheckbox;
